import React, { useState, useEffect } from "react";

export default function TaxReturns() {
  const [year, setYear] = useState("2026");
  const [taxData, setTaxData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // बॅकएंडवरून टॅक्स डेटा फेच करण्यासाठी
  useEffect(() => {
    const fetchTaxData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token"); 
        const response = await fetch(`https://canada-billing.ameontech.com/api/tax-return/summary?year=${year}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) throw new Error("Failed to fetch tax mapping data");
        const data = await response.json();
        setTaxData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTaxData();
  }, [year]);

  if (loading) return <div style={{ padding: "20px", color: "var(--ink-muted)" }}>Loading tax return data...</div>;
  if (error) return <div style={{ padding: "20px", color: "var(--coral)" }}>Error: {error}</div>;
  if (!taxData) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* CSS Styles Inject */}
      <style>{`
        .tax-card { background: #fff; border: 1px solid var(--border); border-radius: 12px; padding: 20px; }
        .tax-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
        .stat-box { padding: 16px; border-radius: 8px; border: 1px solid var(--border-s); }
        .tax-table { width: 100%; border-collapse: collapse; margin-top: 12px; text-align: left; font-size: 14px; }
        .tax-table th { background: #f8fafc; padding: 12px; color: var(--ink-faint); font-weight: 600; border-bottom: 2px solid var(--border); }
        .tax-table td { padding: 12px; border-bottom: 1px solid var(--border); color: var(--ink); }
        .tax-select { padding: 8px 16px; border-radius: 8px; border: 1px solid var(--border); background: #fff; font-size: 14px; font-weight: 600; cursor: pointer; outline: none; }
      `}</style>

      {/* HEADER SECTION */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: "22px", color: "var(--ink)" }}>🍁 Canada Tax Return Mapping</h2>
          <p style={{ fontSize: "13px", color: "var(--ink-faint)", marginTop: "2px" }}>T2125 Business Activities Statement Breakdown</p>
        </div>
        <div>
          <select className="tax-select" value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="2026">Tax Year 2026</option>
            <option value="2025">Tax Year 2025</option>
            <option value="2024">Tax Year 2024</option>
          </select>
        </div>
      </div>

      {/* १. SUMMARY CARDS */}
      <div className="tax-grid">
        <div className="stat-box" style={{ background: "rgba(79,142,247,0.04)", borderColor: "rgba(79,142,247,0.15)" }}>
          <div style={{ fontSize: "12px", color: "var(--blue)", fontWeight: "600", textTransform: "uppercase" }}>Gross Revenue</div>
          <div style={{ fontSize: "24px", fontWeight: "700", marginTop: "6px", color: "var(--ink)" }}>${taxData.summary.total_revenue.toFixed(2)}</div>
        </div>
        <div className="stat-box" style={{ background: "rgba(249,123,107,0.04)", borderColor: "rgba(249,123,107,0.15)" }}>
          <div style={{ fontSize: "12px", color: "var(--coral)", fontWeight: "600", textTransform: "uppercase" }}>Total Expenses</div>
          <div style={{ fontSize: "24px", fontWeight: "700", marginTop: "6px", color: "var(--ink)" }}>${taxData.summary.total_expenses.toFixed(2)}</div>
        </div>
        <div className="stat-box" style={{ background: "rgba(82,201,126,0.04)", borderColor: "rgba(82,201,126,0.15)" }}>
          <div style={{ fontSize: "12px", color: "var(--green)", fontWeight: "600", textTransform: "uppercase" }}>Net Taxable Income</div>
          <div style={{ fontSize: "24px", fontWeight: "700", marginTop: "6px", color: "var(--ink)" }}>${taxData.summary.net_taxable_income.toFixed(2)}</div>
        </div>
      </div>

      {/* २. INCOME & TAX COLLECTED */}
      <div className="tax-card">
        <h3 style={{ fontFamily: "Playfair Display, serif", fontSize: "16px", marginBottom: "4px" }}>Part 1: Gross Sales & CRA Tax Collected</h3>
        <p style={{ fontSize: "12px", color: "var(--ink-faint)" }}>Use these totals for Netfile GST/HST return reporting.</p>
        
        <table className="tax-table">
          <thead>
            <tr>
              <th>CRA Tax Line Description</th>
              <th style={{ textAlign: "right" }}>Amount (CAD)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Gross Sales / Total Invoiced Amount (Excl. Tax)</td>
              <td style={{ textAlign: "right", fontWeight: "600" }}>${taxData.income_mapping.form_line_gross_sales.toFixed(2)}</td>
            </tr>
            <tr>
              <td>GST Collected (5%)</td>
              <td style={{ textAlign: "right" }}>${taxData.income_mapping.tax_collected.gst.toFixed(2)}</td>
            </tr>
            <tr>
              <td>PST Collected (BC, SK)</td>
              <td style={{ textAlign: "right" }}>${taxData.income_mapping.tax_collected.pst.toFixed(2)}</td>
            </tr>
            <tr>
              <td>HST Collected (ON, NB, NL, NS, PE)</td>
              <td style={{ textAlign: "right" }}>${taxData.income_mapping.tax_collected.hst.toFixed(2)}</td>
            </tr>
            {/* ── नवीन प्रांतीय टॅक्स ओळी (QST & RST) ── */}
            <tr>
              <td>QST Collected (Quebec - 9.975%)</td>
              <td style={{ textAlign: "right" }}>${taxData.income_mapping.tax_collected.qst ? taxData.income_mapping.tax_collected.qst.toFixed(2) : "0.00"}</td>
            </tr>
            <tr>
              <td>RST Collected (Manitoba - 7%)</td>
              <td style={{ textAlign: "right" }}>${taxData.income_mapping.tax_collected.rst ? taxData.income_mapping.tax_collected.rst.toFixed(2) : "0.00"}</td>
            </tr>
            {/* ─────────────────────────────────────── */}
            <tr style={{ background: "#fdfdfd", fontWeight: "600" }}>
              <td style={{ color: "var(--blue)" }}>Total Tax Collected</td>
              <td style={{ textAlign: "right", color: "var(--blue)" }}>${taxData.income_mapping.tax_collected.total.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ३. EXPENSE MAPPING (T2125 CATEGORIES) */}
      <div className="tax-card">
        <h3 style={{ fontFamily: "Playfair Display, serif", fontSize: "16px", marginBottom: "4px" }}>Part 2: Expense Mapping (Form T2125)</h3>
        <p style={{ fontSize: "12px", color: "var(--ink-faint)" }}>Expenses grouped into official CRA business categories with estimated Input Tax Credits (ITC).</p>

        <table className="tax-table">
          <thead>
            <tr>
              <th>T2125 Form Category</th>
              <th style={{ textAlign: "right" }}>Total Expense Amount</th>
              <th style={{ textAlign: "right" }}>Estimated ITC Claimable</th>
            </tr>
          </thead>
          <tbody>
            {taxData.expense_mapping.length === 0 ? (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", color: "var(--ink-faint)", padding: "20px" }}>No expenses recorded for this tax year.</td>
              </tr>
            ) : (
              taxData.expense_mapping.map((exp, index) => (
                <tr key={index}>
                  <td style={{ fontWeight: "500" }}>{exp.t2125_category}</td>
                  <td style={{ textAlign: "right", fontWeight: "600" }}>${exp.amount.toFixed(2)}</td>
                  <td style={{ textAlign: "right", color: "var(--green)" }}>${exp.estimated_itc.toFixed(2)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}